import { faAngleDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useRef, useState } from 'react'

export type DropDownItem = {
    id: string
    value: React.ReactNode
}

type Props = {
    items: DropDownItem[]
    label: string
    selectedItemId?: string
    onItemSelected: (id: string) => void
    containerClasses?: string
}

export default function DropDown2({
    items,
    label,
    selectedItemId,
    onItemSelected,
    containerClasses,
}: Props): React.JSX.Element {
    const [isOpen, setIsOpen] = useState(false)
    // Use a ref for the button container instead of the entire component.
    const buttonContainerRef = useRef<HTMLDivElement>(null)

    // Toggle dropdown open/close
    const toggleDropdown = () => setIsOpen((prev) => !prev)

    // Close dropdown when clicking outside of the button container
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                buttonContainerRef.current &&
                !buttonContainerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    function handleItemSelected(item: DropDownItem) {
        onItemSelected(item.id)
        // close the dropdown
        setIsOpen(false)
    }

    return (
        <div className={containerClasses}>
            <div className="relative" ref={buttonContainerRef}>
                <div
                    className={
                        'border border-gray-300 rounded-lg px-2 bg-white hover:border-ctxt-blue hover:bg-gray-100 cursor-pointer min-h-14 pb-2'
                    }
                    onClick={toggleDropdown}
                >
                    {selectedItemId && (
                        <div className="text-gray-700 text-xs mt-1">
                            {label}
                        </div>
                    )}
                    <div className="inline-flex items-center select-none justify-between w-full mt-2">
                        <div className="text-gray-900 text-sm">
                            {selectedItemId
                                ? items.find(
                                      (item) => item.id === selectedItemId,
                                  )?.value
                                : label}
                        </div>
                        <FontAwesomeIcon
                            icon={faAngleDown}
                            className="ml-2 transition-transform duration-200"
                            style={{
                                transform: isOpen
                                    ? 'rotate(180deg)'
                                    : 'rotate(0)',
                            }}
                        />
                    </div>
                </div>

                {/* Dropdown Content */}
                {isOpen && (
                    <div className="absolute left-0 top-full mt-0 w-auto min-w-[150px] bg-white border border-gray-300 shadow-lg rounded-md z-50 max-h-60 overflow-y-auto">
                        <div>
                            {items.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex gap-2 cursor-pointer hover:bg-ctxt-blue-light hover:bg-opacity-10"
                                    onClick={() => handleItemSelected(item)}
                                >
                                    <label className="flex gap-2 py-1 px-2 cursor-pointer">
                                        <div className="text-nowrap text-sm text-gray-900">
                                            {item.value}
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
